import { type ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from 'components/Modal/Modal';
import { BUTTON_VARIANT } from 'constants/interfaces';

import { addKnownListAndFlag, createBuyerAndFlag } from './marketingOpsPaths';

import type { WatchFinding, WatchTowerMockState } from './types';

import styles from './scss/TargetedMarketing.module.scss';

interface IKnownListAddModal {
  open: boolean,
  finding: WatchFinding,
  state: WatchTowerMockState,
  onClose: () => void,
  onApply: (next: WatchTowerMockState) => void,
  onLiveAdd?: (buyerWatchCompanyId: number, watchProductId: number | null) => Promise<void>,
}

function KnownListAddModal({
  open,
  finding,
  state,
  onClose,
  onApply,
  onLiveAdd = undefined,
}: IKnownListAddModal): ReactElement {
  const { t } = useTranslation();
  const sourceId = finding.watchCompanyId;
  const source = state.watchCompanies.find((item) => item.id === sourceId);
  const buyers = useMemo(
    () => state.watchCompanies.filter((item) => (
      item.id !== sourceId && item.status !== 'archived'
    )),
    [sourceId, state.watchCompanies],
  );

  const [buyerId, setBuyerId] = useState<number>(buyers[0]?.id || 0);
  const [isNew, setIsNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUid, setNewUid] = useState('');
  const [newCountry, setNewCountry] = useState('');
  const [productId, setProductId] = useState<number>(0);
  const [confirmed, setConfirmed] = useState(false);

  const onSave = async () => {
    if (!confirmed) {
      toast.error(t('targetedMarketing.knownConfirmRequired'));
      return;
    }
    const watchProductId = productId || null;
    if (onLiveAdd) {
      if (isNew) {
        toast.error(t('targetedMarketing.liveRequestFailed'));
        return;
      }
      try {
        await onLiveAdd(buyerId, watchProductId);
        toast.success(t('targetedMarketing.knownSaved'));
        onClose();
      } catch {
        toast.error(t('targetedMarketing.liveRequestFailed'));
      }
      return;
    }
    const result = isNew
      ? createBuyerAndFlag(state, {
        findingId: finding.id,
        buyer: { name: newName, uid: newUid, addressCountry: newCountry },
        watchProductId,
      })
      : addKnownListAndFlag(state, {
        findingId: finding.id,
        buyerWatchCompanyId: buyerId,
        watchProductId,
      });
    if (!result.ok) {
      toast.error(t(`targetedMarketing.knownError_${result.error}`));
      return;
    }
    onApply(result.state);
    if (!result.createdFlag) {
      toast.info(t('targetedMarketing.knownAlreadyFlagged'));
    } else {
      toast.success(t('targetedMarketing.knownSaved'));
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      title={t('targetedMarketing.addKnownList')}
      className={styles.modalWide}
      description={t('targetedMarketing.knownHint')}
      onClose={onClose}
      onReject={onClose}
      onConfirm={onSave}
      confirmTitle={t('targetedMarketing.knownSave')}
      closeTitle={t('common.cancel')}
      confirmVariant={BUTTON_VARIANT.primary}
      disabledConfirm={!confirmed}
    >
      <div className={styles.formGrid}>
        <label className={styles.checkItem} htmlFor="wt-known-new">
          <input
            id="wt-known-new"
            type="checkbox"
            checked={isNew}
            onChange={(event) => setIsNew(event.target.checked)}
          />
          {t('targetedMarketing.knownCreateBuyer')}
        </label>
        {isNew ? (
          <>
            <div className={styles.field}>
              <label htmlFor="wt-known-name">{t('targetedMarketing.name')}</label>
              <input
                id="wt-known-name"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="wt-known-uid">{t('targetedMarketing.uid')}</label>
              <input
                id="wt-known-uid"
                value={newUid}
                onChange={(event) => setNewUid(event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="wt-known-country">{t('targetedMarketing.addressCountry')}</label>
              <input
                id="wt-known-country"
                value={newCountry}
                maxLength={2}
                onChange={(event) => setNewCountry(event.target.value.toUpperCase())}
              />
            </div>
          </>
        ) : (
          <div className={styles.fieldWide}>
            <label htmlFor="wt-known-buyer">{t('targetedMarketing.knownBuyer')}</label>
            <select
              id="wt-known-buyer"
              value={buyerId}
              onChange={(event) => setBuyerId(Number(event.target.value))}
            >
              {buyers.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className={styles.fieldWide}>
          <label htmlFor="wt-known-product">{t('targetedMarketing.products')}</label>
          <select
            id="wt-known-product"
            value={productId}
            onChange={(event) => setProductId(Number(event.target.value))}
          >
            <option value={0}>{t('targetedMarketing.knownNoProduct')}</option>
            {(source?.products || []).map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </div>
        <label className={`${styles.checkItem} ${styles.fieldWide}`} htmlFor="wt-known-confirm">
          <input
            id="wt-known-confirm"
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          {t('targetedMarketing.knownConfirm', { source: source?.name || '' })}
        </label>
      </div>
    </Modal>
  );
}

KnownListAddModal.defaultProps = {
  onLiveAdd: undefined,
};

export default KnownListAddModal;
